import hasAbortController from "@mgdx/shared/src/utils/hasAbortController";
import type { DOMExceptionWithRequestUrl, TypeErrorWithRequestInfo } from "@mgdx-libs/error-handler";

const getRequestUrl = (input: Parameters<typeof fetch>[0]): string | undefined => {
  if (typeof input === "string") {
    return input;
  }

  if (input instanceof URL) {
    return input.toString();
  }

  if (input instanceof Request) {
    return input.url;
  }

  return undefined;
};

const toAbortReasonString = (reason: unknown): string | undefined => {
  if (typeof reason === "string") return reason;
  if (reason === undefined || reason === null) return undefined;

  try {
    return JSON.stringify(reason);
  } catch {
    return String(reason);
  }
};

const attachAbortMetadataToError = (
  error: unknown,
  metadata: Pick<DOMExceptionWithRequestUrl, "requestUrl" | "apiName" | "abortReason" | "abortSource">
) => {
  if (error instanceof DOMException && error.name === "AbortError") {
    const domException = error as DOMExceptionWithRequestUrl;
    Object.assign(domException, {
      requestUrl: domException.requestUrl ?? metadata.requestUrl,
      apiName: domException.apiName ?? metadata.apiName,
      abortReason: domException.abortReason ?? metadata.abortReason,
      abortSource: domException.abortSource ?? metadata.abortSource,
    });
  }
};

// 通信断の TypeError はブラウザが原因を伏せるため、切り分けに使う失敗時の状況を付ける
const attachRequestInfoToTypeError = (
  error: unknown,
  requestInfo: Pick<TypeErrorWithRequestInfo, "requestUrl" | "apiName" | "method" | "elapsedMs">
) => {
  if (error instanceof TypeError) {
    const typeError = error as TypeErrorWithRequestInfo;
    // URL にはクエリのトークンなどが入りうるため、Sentry が console の引数を breadcrumb に記録しても拾われないよう列挙しない
    Object.defineProperty(typeError, "requestUrl", {
      value: typeError.requestUrl ?? requestInfo.requestUrl,
      enumerable: false,
      configurable: true,
    });
    Object.assign(typeError, {
      apiName: typeError.apiName ?? requestInfo.apiName,
      method: typeError.method ?? requestInfo.method,
      elapsedMs: typeError.elapsedMs ?? requestInfo.elapsedMs,
      online: typeError.online ?? navigator.onLine,
      visibilityState: typeError.visibilityState ?? document.visibilityState,
    });
  }
};

const mergeAbortSignals = (
  timeoutSignal: AbortSignal,
  callerSignal: AbortSignal | undefined,
  onAbort: (source: "timeout" | "caller") => void
): { signal: AbortSignal; cleanup: () => void } => {
  const controller = new AbortController();
  const cleanupFns: Array<() => void> = [];

  const abortWithSource = (source: "timeout" | "caller", reason: unknown) => {
    if (controller.signal.aborted) return;
    onAbort(source);
    controller.abort(reason);
  };

  const bindSignal = (signal: AbortSignal, source: "timeout" | "caller") => {
    if (signal.aborted) {
      const abortedReason = (signal as AbortSignal & { reason?: unknown }).reason;
      abortWithSource(source, abortedReason);
      return;
    }

    const listener = () => {
      const abortedReason = (signal as AbortSignal & { reason?: unknown }).reason;
      abortWithSource(source, abortedReason);
    };

    signal.addEventListener("abort", listener, { once: true });
    cleanupFns.push(() => signal.removeEventListener("abort", listener));
  };

  bindSignal(timeoutSignal, "timeout");
  if (callerSignal) {
    bindSignal(callerSignal, "caller");
  }

  return {
    signal: controller.signal,
    cleanup: () => {
      cleanupFns.forEach((cleanup) => cleanup());
    },
  };
};

export const createFetchTimeout =
  (timeout: number, apiName?: string): WindowOrWorkerGlobalScope["fetch"] =>
  (input, init): Promise<Response> => {
    const fetch = window.fetch.bind(window);

    if (hasAbortController()) {
      const timeoutController = new AbortController();
      const requestUrl = getRequestUrl(input);
      const timeoutReason = `Fetch ${apiName || "unknownApi"} timeout (${timeout}ms)`;
      let abortSource: "timeout" | "caller" | "unknown" = "unknown";
      const callerSignal = init?.signal ?? undefined;
      const { signal, cleanup } = mergeAbortSignals(timeoutController.signal, callerSignal, (source) => {
        abortSource = source;
      });

      const timerId = setTimeout(() => {
        timeoutController.abort(timeoutReason);
      }, timeout);
      const startedAt = Date.now();

      return fetch(input, {
        ...init,
        signal,
      })
        .catch((error) => {
          attachAbortMetadataToError(error, {
            requestUrl,
            apiName,
            abortSource,
            abortReason: toAbortReasonString((signal as AbortSignal & { reason?: unknown }).reason),
          });
          attachRequestInfoToTypeError(error, {
            requestUrl,
            apiName,
            method: init?.method ?? "GET",
            elapsedMs: Date.now() - startedAt,
          });
          throw error;
        })
        .finally(() => {
          clearTimeout(timerId);
          cleanup();
        });
    }

    return fetch(input, {
      ...init,
    });
  };
